/**
 * 공개(무인증 토큰) API 공용 클라이언트 — DLR(dlr-external)·COMPOSED(composed-external) 공유.
 *
 * 무인증(토큰 기반) 경로 — axios 인증 인스턴스가 아닌 순수 fetch를 쓴다(인증 헤더 미부착).
 * 서버 응답 봉투 `{ success, data, error }`를 해체해 성공 시 data만, 실패 시 봉투를 throw한다
 * (페이지의 에러 코드 분기·apiErrorMessage가 봉투 형상을 읽는다).
 */

export async function publicTokenRequest<T = void>(
  url: string,
  options?: RequestInit,
): Promise<T> {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const body = await res.json();
  if (!res.ok || !body.success) {
    throw body;
  }
  return body.data as T;
}

/**
 * 공개 담당자 등록 입력 — 서버 DTO(CreateSiteContactByTokenDto)와 형상 동일(whitelist 계약).
 * DLR·COMPOSED 공개 site-contacts 엔드포인트가 같은 DTO·코어(cap 10)를 쓴다.
 */
export interface CreateSiteContactByTokenInput {
  name: string;
  /** 필수 — 현장 담당자 생성 경로의 공통 규약(중복 판정 정본 키). 서버 DTO가 형식까지 검증. */
  phone: string;
  department?: string;
  position?: string;
  email?: string;
}

/**
 * API 에러 봉투에서 사용자에게 보일 문구 추출 — 커스텀 문구는 details.message에 실린다.
 *
 * ⛔ **같은 이름이 세 자리이고 사본이 아니다** — 이것은 **공개 토큰 봉투 전용**이라
 * `extractApiError`를 경유하지 않고 `error.details.message`를 먼저 읽는다. 분간표(읽는 봉투 ·
 * 폴백 소유 · 반환형)는 `lib/api.ts`의 `apiErrorMessage` JSDoc이 정본이다 — 여기 베껴 적지 않는다.
 * 그 표의 한 줄만 옮긴다: **섞어 쓰는 것은 tsc가 막고(`fallback`이 required라 `TS2554`) 그 집행이
 * 이 매개변수의 required 하나에 매달려 있다** ⇒ ⛔ `fallback?: string`으로 바꾸지 말 것.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const apiErr = (err as { error?: { message?: string; details?: { message?: string } } })?.error;
  return apiErr?.details?.message ?? apiErr?.message ?? fallback;
}
